/**
 * 파일명: termsModal.js
 * 작성자: 김준형
 * 작성일자: 26.08.13
 * 설명: 약관·방침 전문 모달. 문서 데이터는 components/common/docs/ 에 위치함
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import style from "css/terms.module.css";

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        d="M4.5 4.5l11 11M15.5 4.5l-11 11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function renderBlock(block, index) {
  switch (block.type) {
    case "heading":
      return (
        <h4
          key={index}
          className={style.terms_heading}
        >
          {block.text}
        </h4>
      );
    case "sub":
      return (
        <p
          key={index}
          className={style.terms_sub}
        >
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul
          key={index}
          className={style.terms_list}
        >
          {block.items.map((item) => (
            <li
              key={item}
              className={style.terms_item}
            >
              {item}
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <p
          key={index}
          className={style.terms_text}
        >
          {block.text}
        </p>
      );
  }
}

/**
 * @param {object}   doc      { title, blocks }
 * @param {function} onAgree  넘기면 확인 버튼이 동의 버튼으로 바뀐다
 */
function TermsModal({ open, doc, onClose, onAgree }) {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    if (bodyRef.current) bodyRef.current.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || !doc) return null;

  return createPortal(
    <div
      className={style.terms_dim}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={style.terms_modal}
        role="dialog"
        aria-modal="true"
        aria-label={doc.title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={style.terms_head}>
          <h3 className={style.terms_title}>{doc.title}</h3>
          <button
            type="button"
            className={style.terms_close}
            onClick={onClose}
            aria-label="닫기"
          >
            <CloseIcon />
          </button>
        </div>

        <div
          className={style.terms_body}
          ref={bodyRef}
          tabIndex={-1}
        >
          {doc.blocks.map(renderBlock)}
        </div>

        <div className={style.terms_foot}>
          {onAgree ? (
            <button
              type="button"
              className={style.terms_confirm}
              onClick={onAgree}
            >
              위 내용에 동의합니다
            </button>
          ) : (
            <button
              type="button"
              className={style.terms_confirm}
              onClick={onClose}
            >
              확인
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default TermsModal;
